import { supabase } from "@/lib/supabase";

export type SearchContainerResult = {
  id: string;
  name: string | null;
  slug: string | null;
  container_type: "document" | "room" | "message_room";
  parent_container_id: string | null;
  description: string | null;
  summary: string | null;
  /** CR-97: an unedited (blank-named) shared message room is titled "All project members". */
  is_shared: boolean | null;
};

export type SearchMessageResult = {
  id: string;
  content: string;
  room: string;
  created_at: string | null;
  full_name: string | null;
  room_name: string | null;
  room_parent_container_id: string | null;
  /** CR-97: lets the palette resolve a blank room name to its derived title. */
  room_is_shared: boolean | null;
  room_container_type: "room" | "message_room" | null;
};

export async function searchWorkspaceContainers(
  projectIds: string[],
  query: string,
): Promise<SearchContainerResult[]> {
  if (!projectIds.length || !query.trim()) return [];

  const { data, error } = await supabase
    .from("containers")
    .select("id, name, slug, container_type, parent_container_id, description, summary, is_shared")
    .in("container_type", ["document", "room", "message_room"])
    .in("parent_container_id", projectIds)
    .or(`name.ilike.%${query}%,summary.ilike.%${query}%,description.ilike.%${query}%`)
    .is("deleted_at", null)
    .order("name")
    .limit(20);

  if (error) throw error;
  return data as SearchContainerResult[];
}

export type SearchDocContentResult = {
  document_id: string;
  document_name: string;
  project_id: string;
  matched_line: string | null;
};

export async function searchDocumentContent(
  projectIds: string[],
  query: string,
): Promise<SearchDocContentResult[]> {
  if (!projectIds.length || !query.trim()) return [];

  const { data, error } = await supabase.rpc("search_document_plain_text", {
    p_project_ids: projectIds,
    p_query: query,
    p_limit: 10,
  });

  if (error) throw error;
  return data as SearchDocContentResult[];
}

export async function searchWorkspaceMessages(
  projectIds: string[],
  query: string,
): Promise<SearchMessageResult[]> {
  if (!projectIds.length || !query.trim()) return [];

  const { data: rooms, error: roomsError } = await supabase
    .from("containers")
    .select("id, name, parent_container_id, is_shared, container_type")
    .in("container_type", ["room", "message_room"])
    .in("parent_container_id", projectIds)
    .is("deleted_at", null);

  if (roomsError) throw roomsError;
  if (!rooms.length) return [];

  const roomIds = rooms.map((r) => r.id);
  const roomMap = new Map(rooms.map((r) => [r.id, r]));

  const { data: messages, error } = await supabase
    .from("message_with_author")
    .select("id, content, room, created_at, full_name")
    .in("room", roomIds)
    .ilike("content", `%${query}%`)
    .order("created_at", { ascending: false })
    .limit(10);

  if (error) throw error;

  type ValidMsg = (typeof messages)[number] & {
    id: string;
    content: string;
    room: string;
  };

  return messages
    .filter((msg): msg is ValidMsg => !!(msg.id && msg.content && msg.room))
    .map((msg) => {
      const roomContainer = roomMap.get(msg.room);
      return {
        id: msg.id,
        content: msg.content,
        room: msg.room,
        created_at: msg.created_at,
        full_name: msg.full_name,
        room_name: roomContainer?.name ?? null,
        room_parent_container_id: roomContainer?.parent_container_id ?? null,
        room_is_shared: roomContainer?.is_shared ?? null,
        room_container_type:
          roomContainer?.container_type === "message_room" ||
          roomContainer?.container_type === "room"
            ? roomContainer.container_type
            : null,
      };
    });
}
